import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trophy, ChefHat, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { useDict } from "@/i18n/dict";

const RANK_BG = ["bg-[color:var(--mango)]", "bg-[color:var(--bg-2)]", "bg-[#c47a3a]/30"];
const INITIALS = (name = "") =>
  name.split(/\s+/).filter(Boolean).map((p) => p[0]).slice(0, 2).join("").toUpperCase() || "BA";

/**
 * Editorial CTA section — invite anyone to share their recipe and climb the
 * monthly leaderboard. Replaces the bare 'submit a recipe' callout with the
 * full gamification narrative. Shows a miniature Top-3 podium next to the CTA.
 */
export default function BecomeChefSection() {
  const { lang } = useLang();
  const t = useDict(lang);
  const [rows, setRows] = useState([]);

  useEffect(() => {
    let cancel = false;
    api
      .leaderboard("month", 3)
      .then((r) => { if (!cancel) setRows(r.rows || []); })
      .catch(() => {});
    return () => { cancel = true; };
  }, []);

  const podium = rows.length >= 1 ? rows : [
    // graceful placeholders so the section looks alive even with no data
    { user_id: "p1", name: "À vous le top !", points: 0, tier: "goûteur" },
    { user_id: "p2", name: "Ton nom ici", points: 0, tier: "apprenti" },
    { user_id: "p3", name: "Ta première recette", points: 0, tier: "goûteur" },
  ];

  return (
    <section
      data-testid="become-chef-section"
      className="max-w-[1400px] mx-auto px-6 lg:px-12 mt-32 mb-12 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center"
    >
      <div className="lg:col-span-7">
        <p className="eyebrow mb-3 flex items-center gap-3">
          <ChefHat size={14} color="var(--red)" strokeWidth={1.5} />
          {t.becomeChef.eyebrow}
        </p>
        <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight mt-2 mb-6">
          {t.becomeChef.title}
          <br />
          <em className="text-[color:var(--red)] not-italic font-serif-display">{t.becomeChef.titleAccent}</em>
        </h2>
        <p className="text-base text-[color:var(--ink-2)] max-w-xl leading-relaxed mb-8">
          {t.becomeChef.body}
        </p>
        <ul className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
          <li className="border-l-2 border-[color:var(--red)] pl-4">
            <p className="font-serif-display text-3xl">+50 pts</p>
            <p className="text-xs uppercase tracking-[0.18em] text-[color:var(--ink-3)] mt-1">{t.becomeChef.points.recipe}</p>
          </li>
          <li className="border-l-2 border-[color:var(--red)] pl-4">
            <p className="font-serif-display text-3xl">+10 pts</p>
            <p className="text-xs uppercase tracking-[0.18em] text-[color:var(--ink-3)] mt-1">{t.becomeChef.points.review}</p>
          </li>
          <li className="border-l-2 border-[color:var(--red)] pl-4">
            <p className="font-serif-display text-3xl">+100 pts</p>
            <p className="text-xs uppercase tracking-[0.18em] text-[color:var(--ink-3)] mt-1">{t.becomeChef.points.referral}</p>
          </li>
        </ul>
        <div className="flex flex-wrap gap-4">
          <Link to="/submit" data-testid="become-chef-submit-cta" className="btn-navy">
            {t.becomeChef.cta} <ArrowRight size={14} />
          </Link>
          <Link to="/leaderboard" data-testid="become-chef-leaderboard-cta" className="btn-outline">
            {t.becomeChef.seeRanking}
          </Link>
        </div>
      </div>

      <div className="lg:col-span-5">
        <div className="border border-[color:rgba(0,16,48,0.12)] bg-white p-6 lg:p-8 relative">
          <div className="flex items-center gap-3 mb-6">
            <Trophy size={18} color="var(--red)" strokeWidth={1.5} />
            <p className="eyebrow">{t.becomeChef.podiumTitle}</p>
          </div>
          <ol className="space-y-4">
            {podium.slice(0, 3).map((r, i) => (
              <li
                key={r.user_id}
                data-testid={`podium-${i + 1}`}
                className={`flex items-center gap-4 p-3 ${i === 0 ? "border-2 border-[color:var(--red)]" : "border border-[color:rgba(0,16,48,0.1)]"}`}
              >
                <span className={`w-10 h-10 flex items-center justify-center font-serif-display text-xl text-[color:var(--navy)] ${RANK_BG[i] || "bg-[var(--bg-2)]"}`}>
                  {i + 1}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium leading-tight truncate">{r.name}</p>
                  <p className="eyebrow mt-0.5">{t.tier[r.tier] || r.tier}</p>
                </div>
                <div className="text-right">
                  <p className="font-serif-display text-2xl leading-none">{r.points}</p>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[color:var(--ink-3)] mt-0.5">pts</p>
                </div>
              </li>
            ))}
          </ol>
          <p className="text-xs text-[color:var(--ink-3)] mt-6 italic">
            {t.becomeChef.podiumNote}
          </p>
        </div>
      </div>
    </section>
  );
}
